/**
 * Financial Periods Component
 * Manages financial periods with proper API integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { financialPeriodService } from '../../services/financialPeriodService';
import { useRBAC } from '../../hooks/useRBAC';
import { getQuickFilters } from '../../utils/filterUtils';
import ProfessionalLoader from "../common/ProfessionalLoader";
import FinancialPeriodForm from './FinancialPeriodForm';
import FinancialPeriodFilters from './FinancialPeriodFilters';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedStats from '../common/UnifiedStats';
import UnifiedFilters from '../common/UnifiedFilters';
import UnifiedActionPanel from '../common/UnifiedActionPanel';
import UnifiedTable from '../common/UnifiedTable';
import UnifiedEmptyState from '../common/UnifiedEmptyState';
import './FinancialPeriods.css';

const FinancialPeriods = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageIntegrations } = useRBAC();

  // State management
  const [periods, setPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [selectedPeriods, setSelectedPeriods] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    is_active: '',
    status: '',
    start_date_after: '',
    start_date_before: '',
    end_date_after: '',
    end_date_before: '',
    ordering: '-start_date'
  });
  const [searchInput, setSearchInput] = useState('');

  // Statistics
  const [stats, setStats] = useState({
    total_periods: 0,
    active_periods: 0,
    upcoming_periods: 0,
    completed_periods: 0
  });

  // Load periods
  const loadPeriods = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await financialPeriodService.getFinancialPeriods(filters);

      if (response.success) {
        const periodsData = response.data || [];
        setPeriods(periodsData);
        updateStatistics(periodsData);
      } else {
        showError(response.message || 'Failed to load financial periods', 'Error');
      }
    } catch (error) {
      showError('Failed to load financial periods', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [filters, showError]);

  // Update statistics
  const updateStatistics = (periodsData) => {
    const now = new Date();
    const stats = {
      total_periods: periodsData.length,
      active_periods: periodsData.filter(period => period.is_active).length,
      upcoming_periods: periodsData.filter(period => {
        if (!period.is_active) return false;
        const startDate = new Date(period.start_date);
        return startDate > now;
      }).length,
      completed_periods: periodsData.filter(period => {
        const endDate = new Date(period.end_date);
        return endDate < now;
      }).length
    };
    setStats(stats);
  };

  // Load periods on component mount
  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // Handle create period
  const handleCreatePeriod = async (periodData) => {
    setIsProcessing(true);
    try {
      const response = await financialPeriodService.createFinancialPeriod(periodData);

      if (response.success) {
        showSuccess('Financial period created successfully', 'Success');
        setShowForm(false);
        loadPeriods();
      } else {
        showError(response.message || 'Failed to create period', 'Error');
      }
    } catch (error) {
      showError('Failed to create period', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle update period
  const handleUpdatePeriod = async (periodId, periodData) => {
    setIsProcessing(true);
    try {
      const response = await financialPeriodService.updateFinancialPeriod(periodId, periodData);

      if (response.success) {
        showSuccess('Financial period updated successfully', 'Success');
        setEditingPeriod(null);
        loadPeriods();
      } else {
        showError(response.message || 'Failed to update period', 'Error');
      }
    } catch (error) {
      showError('Failed to update period', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };


  // Handle delete period
  const handleDeletePeriod = async (periodId) => {
    if (!window.confirm('Are you sure you want to delete this financial period?')) {
      return;
    }

    setIsProcessing(true);
    try {
      const response = await financialPeriodService.deleteFinancialPeriod(periodId);

      if (response.success) {
        showSuccess('Financial period deleted successfully', 'Success');
        loadPeriods();
      } else {
        showError(response.message || 'Failed to delete period', 'Error');
      }
    } catch (error) {
      showError('Failed to delete period', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };


  // Handle period selection
  const handlePeriodSelect = (periodId, isSelected) => {
    setSelectedPeriods(prev =>
      isSelected
        ? [...prev, periodId]
        : prev.filter(id => id !== periodId)
    );
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedPeriods(periods.map(period => period.id));
    } else {
      setSelectedPeriods([]);
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSearchInputChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    setFilters(prev => ({ ...prev, search: searchInput }));
  }, [searchInput]);

  if (isLoading) {
    return (
      <div className="financial-periods">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="financial-periods">
      {/* Financial Periods Management Section */}
      <div className="periods-management-container">
        {/* Unified Header */}
        <UnifiedHeader
          title="Financial Periods"
          subtitle="Manage financial periods for the organization"
          icon="📅"
          count={stats.total_periods}
          countLabel="Total Periods"
          selectedCount={selectedPeriods.length}
          selectedLabel="Selected"
        >
          {canManageIntegrations && (
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
              disabled={isProcessing}
            >
              ➕ Create Period
            </button>
          )}
        </UnifiedHeader>

        {/* Unified Statistics */}
        <UnifiedStats
          stats={[
            { title: '📅 Total Periods', value: stats.total_periods },
            { title: '✅ Active Periods', value: stats.active_periods },
            { title: '⏳ Upcoming', value: stats.upcoming_periods },
            { title: '✅ Completed', value: stats.completed_periods }
          ]}
        />

        {/* Unified Filters */}
        <UnifiedFilters
          searchValue={searchInput}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          quickFilters={getQuickFilters('financialPeriods', filters)}
          onQuickFilterClick={(filterKey) => {
            if (filterKey === 'all') {
              handleFilterChange({
                search: '',
                is_active: '',
                status: '',
                start_date_after: '',
                start_date_before: '',
                end_date_after: '',
                end_date_before: '',
                ordering: '-start_date'
              });
            } else if (filterKey === 'active') {
              handleFilterChange({
                ...filters,
                is_active: filters.is_active === 'true' ? '' : 'true'
              });
            } else {
              handleFilterChange({
                ...filters,
                status: filters.status === filterKey ? '' : filterKey
              });
            }
          }}
        />

        {/* Unified Action Panel */}
        <UnifiedActionPanel
          selectedCount={selectedPeriods.length}
          totalCount={periods.length}
          selectedItemsLabel="periods"
          onSelectAll={handleSelectAll}
          onClearSelection={() => setSelectedPeriods([])}
          selectedAction=""
          onActionChange={() => {
            // Implement bulk actions
            showSuccess('Bulk actions functionality coming soon', 'Info');
          }}
          onExecuteAction={() => {
            showSuccess('Bulk actions functionality coming soon', 'Info');
          }}
          isProcessing={isProcessing}
          actionOptions={[
            ...(canManageIntegrations ? [
              { value: 'activate', label: '✅ Activate Periods', disabled: false },
              { value: 'deactivate', label: '❌ Deactivate Periods', disabled: false },
              { value: 'delete', label: '🗑️ Delete Periods', disabled: false }
            ] : [])
          ]}
        />

        {/* Financial Periods Table */}
        {periods.length > 0 ? (
          <UnifiedTable
            columns={[
              { key: 'id', header: 'ID', className: 'unified-table-select-header' },
              { key: 'name', header: 'Name' },
              { key: 'start_date', header: 'Start Date' },
              { key: 'end_date', header: 'End Date' },
              { key: 'status', header: 'Status' },
              { key: 'created_at', header: 'Created' }
            ]}
            data={periods}
            onRowClick={(period) => {
              // Handle row click - could open edit modal or view details
              console.log('Period clicked:', period);
            }}
            onSelectRow={handlePeriodSelect}
            selectedRows={selectedPeriods}
            renderCell={(column, period) => {
              switch (column.key) {
                case 'id':
                  return <span className="unified-period-id">{period.id}</span>;
                case 'name':
                  return <span className="unified-period-name">{period.name}</span>;
                case 'start_date':
                  return new Date(period.start_date).toLocaleDateString();
                case 'end_date':
                  return new Date(period.end_date).toLocaleDateString();
                case 'status':
                  return (
                    <span className={`unified-status-badge ${period.is_active ? 'active' : 'inactive'}`}>
                      {period.is_active ? 'Active' : 'Inactive'}
                    </span>
                  );
                case 'created_at':
                  return period.created_at
                    ? new Date(period.created_at).toLocaleDateString()
                    : 'N/A';
                default:
                  return period[column.key];
              }
            }}
          />
        ) : (
          <UnifiedEmptyState
            icon="📅"
            title="No periods found"
            description="Try adjusting your search criteria or create a new period."
            actions={[
              ...(canManageIntegrations ? [{
                label: 'Create First Period',
                icon: '➕',
                onClick: () => setShowForm(true),
                variant: 'primary'
              }] : [])
            ]}
          />
        )}
      </div>


      {/* Period Form Modal */}
      {showForm && (
        <FinancialPeriodForm
          period={editingPeriod}
          onSubmit={editingPeriod ? handleUpdatePeriod : handleCreatePeriod}
          onClose={() => {
            setShowForm(false);
            setEditingPeriod(null);
          }}
          isSubmitting={isProcessing}
        />
      )}
    </div>
  );
};

export default FinancialPeriods;